import React, { useState, useEffect } from 'react';
import './About.css';
import profileImage from '../../assets/images/Hamza.jpg';

const DeveloperDayAnimation = () => {
    const [currentActivity, setCurrentActivity] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [coffeeCount, setCoffeeCount] = useState(0);

    const activities = [
        {
            time: "9:00 AM",
            activity: "Setting up Django environment",
            emoji: "🐍",
            mood: "motivated",
            description: "Let's build some APIs today!"
        },
        {
            time: "10:30 AM",
            activity: "Writing database migrations",
            emoji: "🗄️",
            mood: "focused",
            description: "ForeignKey relationships everywhere..."
        },
        {
            time: "12:00 PM",
            activity: "Explaining REST to frontend dev",
            emoji: "🤯",
            mood: "patient",
            description: "No, GET requests don't have bodies!"
        },
        {
            time: "1:00 PM",
            activity: "Lunch break (still thinking about code)",
            emoji: "🍕",
            mood: "multitasking",
            description: "How to optimize that query..."
        },
        {
            time: "2:00 PM",
            activity: "Coffee break #3",
            emoji: "☕",
            mood: "caffeinated",
            description: "Backend runs on caffeine!"
        },
        {
            time: "3:30 PM",
            activity: "Debugging 500 server error",
            emoji: "🔥",
            mood: "detective",
            description: "The plot thickens..."
        },
        {
            time: "5:00 PM",
            activity: "Found the bug: missing comma",
            emoji: "🐛",
            mood: "facepalm",
            description: "Python syntax strikes again!"
        },
        {
            time: "6:00 PM",
            activity: "Writing unit tests",
            emoji: "🧪",
            mood: "responsible",
            description: "Testing is not optional!"
        },
        {
            time: "7:30 PM",
            activity: "Deploying to production",
            emoji: "🚀",
            mood: "nervous",
            description: "Please don't break anything..."
        },
        {
            time: "8:00 PM",
            activity: "Everything works! Going home",
            emoji: "🎉",
            mood: "victorious",
            description: "Another day, another API!"
        }
    ];


    useEffect(() => {
        if (isActive) {
            const interval = setInterval(() => {
                setCurrentActivity(prev => {
                    if (prev >= activities.length - 1) {
                        setTimeout(() => {
                            setIsActive(false);
                            setCurrentActivity(0);
                            setCoffeeCount(0);
                        }, 3000);
                        return prev;
                    }
                    return prev + 1;
                });
            }, 2000);

            return () => clearInterval(interval);
        }
    }, [isActive]);

    useEffect(() => {
        if (activities[currentActivity]?.emoji === "☕") {
            setCoffeeCount(prev => prev + 1);
        }
    }, [currentActivity]);

    const getMoodColor = (mood) => {
        const colors = {
            optimistic: "#4ade80",
            confused: "#fbbf24",
            stressed: "#f87171",
            caffeinated: "#fb923c",
            panicked: "#dc2626",
            victorious: "#c084fc",
            hyperfocused: "#60a5fa"
        };
        return colors[mood] || "#9ca3af";
    };

    return (
        <div className="developer-day-animation">
            <div className="animation-container">
                <div className="animation-header">
                    <h4 className="animation-title">A Day in My Life</h4>
                    <p className="animation-subtitle">The real developer experience</p>

                    {!isActive && (
                        <button
                            onClick={() => setIsActive(true)}
                            className="start-day-btn"
                        >
                            Start My Day 🚀
                        </button>
                    )}
                </div>

                {/* Coffee Counter */}
                <div className="coffee-counter">
                    <div className="coffee-counter-content">
                        <span className="coffee-icon">☕</span>
                        <span className="coffee-text">Coffee Count: {coffeeCount}</span>
                    </div>
                </div>

                {/* Activity Display */}
                {isActive && (
                    <div className="activities-list">
                        {activities.map((activity, index) => (
                            <div
                                key={index}
                                className={`activity-item ${index === currentActivity ? 'active' :
                                    index < currentActivity ? 'completed' : 'upcoming'
                                    }`}
                            >
                                <div className={`activity-emoji ${index === currentActivity ? 'bouncing' : ''}`}>
                                    {activity.emoji}
                                </div>

                                <div className="activity-content">
                                    <div className="activity-meta">
                                        <span className="activity-time">{activity.time}</span>
                                        <span
                                            className="activity-mood"
                                            style={{ color: getMoodColor(activity.mood) }}
                                        >
                                            {activity.mood}
                                        </span>
                                    </div>
                                    <p className="activity-name">{activity.activity}</p>
                                    <p className="activity-description">{activity.description}</p>
                                </div>

                                {index === currentActivity && (
                                    <div className="activity-indicator">
                                        <svg className="indicator-icon" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Completed Message */}
                {!isActive && currentActivity > 0 && (
                    <div className="completion-message">
                        <div className="completion-emoji">😴</div>
                        <p className="completion-text">Another productive day! Time to sleep and dream about code...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const About = () => {
    return (
        <section id="about" className="container">
            <div className="section-header">
                <h2 className="section-title">About Me</h2>
                <p className="section-subtitle">Dedicated to building exceptional backend solutions</p>
            </div>
            <div className="about-content">
                <div className="about-image">
                    <div className="profile-image-wrapper">
                        <img
                            src={profileImage}
                            alt="Hamza Ekhwan - Backend Developer"
                            className="profile-image"
                        />
                    </div>
                </div>
                <div className="about-text">
                    <h3>Backend Development Expert</h3>
                    <p>
                        With a strong foundation in Django and Python, I specialize in creating robust backend systems
                        that power modern web applications. My expertise lies in building RESTful APIs, designing
                        efficient database schemas, and implementing secure authentication systems.
                    </p>
                    <p>
                        I'm passionate about writing clean, maintainable code and following best practices.
                        My goal is to create backend solutions that are not only functional but also scalable
                        and optimized for performance.
                    </p>
                </div>
            </div>
            <div className="stats">
                <div className="stat-item">
                    <div className="stat-number">4+</div>
                    <div className="stat-label">Years Experience</div>
                </div>
                <div className="stat-item">
                    <div className="stat-number">60+</div>
                    <div className="stat-label">Projects Completed</div>
                </div>
                <div className="stat-item">
                    <div className="stat-number">55+</div>
                    <div className="stat-label">Clients</div>
                </div>
            </div>
            {/* Developer Day Animation */}
            <DeveloperDayAnimation />


        </section>
    );
};

export default About;